import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { AnalyticsService } from 'services';
import { LawyerMembershipIcon, UserMembershipIcon } from 'assets/icons';
import { DateRange } from '../hooks/use-date-range';
import { buildDateCategoryAxis } from '../utils/chart-axis';
import { buildGradientFill } from '../utils/chart-style';
import SectionHeader from '../components/section-header';
import ChartCard from '../components/chart-card';
import KpiCard from '../components/kpi-card';

interface Props {
  range: DateRange;
}

const DONUT_COLORS = ['#0777ff', '#49aa43', '#ef6526', '#40457d', '#0af4fc', '#d20b06'];

const sum = (rows: any[], key: string) => rows.reduce((acc, r) => acc + (r[key] || 0), 0);

const MembershipsSection = ({ range }: Props) => {
  const [loadingActive, setLoadingActive] = useState(true);
  const [active, setActive] = useState<{ lawyers: any[]; users: any[] }>({
    lawyers: [],
    users: [],
  });

  const [loadingHistory, setLoadingHistory] = useState(true);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingActive(true);
      try {
        // No depende del filtro de fecha -- es la foto de "activas ahora".
        const res: any = await AnalyticsService.memberships.active({ withoutLoading: true });
        if (!cancelled) setActive({ lawyers: res?.lawyers || [], users: res?.users || [] });
      } catch (error) {
        console.error('[MembershipsSection] Error cargando membresías activas', error);
      } finally {
        if (!cancelled) setLoadingActive(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const res: any = await AnalyticsService.memberships.purchaseHistory({
          ...range,
          withoutLoading: true,
        });
        if (!cancelled) setHistory(res || []);
      } catch (error) {
        console.error('[MembershipsSection] Error cargando histórico de compras', error);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.startDate, range.endDate]);

  const categories = history.map((r) => moment(r.date).format('DD/MM'));
  const lawyerPurchasesTotal = sum(history, 'lawyerPurchases');
  const userPurchasesTotal = sum(history, 'userPurchases');

  return (
    <>
      <section className='analytics-section'>
        <SectionHeader
          title='Membresías activas ahora'
          subtitle='Usuarios con una membresía vigente en este momento, por tipo'
        />
        <div className='grid grid-2'>
          <ChartCard
            title='Abogados'
            loading={loadingActive}
            empty={!loadingActive && active.lawyers.length === 0}
            type='donut'
            height={300}
            series={active.lawyers.map((m) => m.users)}
            options={{
              labels: active.lawyers.map((m) => m.name),
              colors: DONUT_COLORS,
              legend: { position: 'bottom' },
              dataLabels: { enabled: false },
            }}
          />
          <ChartCard
            title='Clientes'
            loading={loadingActive}
            empty={!loadingActive && active.users.length === 0}
            type='donut'
            height={300}
            series={active.users.map((m) => m.users)}
            options={{
              labels: active.users.map((m) => m.name),
              colors: DONUT_COLORS,
              legend: { position: 'bottom' },
              dataLabels: { enabled: false },
            }}
          />
        </div>
      </section>

      <section className='analytics-section'>
        <SectionHeader
          title='Compras de membresía'
          subtitle='Ritmo diario de compras en el rango seleccionado'
        />
        <div className='grid grid-asym'>
          <ChartCard
            title='Compras por día'
            loading={loadingHistory}
            empty={!loadingHistory && history.length === 0}
            type='area'
            height={380}
            series={[
              { name: 'Abogados', data: history.map((r) => r.lawyerPurchases) },
              { name: 'Clientes', data: history.map((r) => r.userPurchases) },
            ]}
            options={{
              chart: { toolbar: { show: false }, zoom: { enabled: false } },
              dataLabels: { enabled: false },
              stroke: { curve: 'smooth', width: 3 },
              xaxis: buildDateCategoryAxis(categories),
              legend: { position: 'top', horizontalAlign: 'right' },
              ...buildGradientFill(['#7f00b2', '#49aa43']),
            }}
          />
          <div className='side-stack'>
            <KpiCard
              label='Compras de abogados'
              value={lawyerPurchasesTotal}
              icon={LawyerMembershipIcon}
              accent='purple'
            />
            <KpiCard
              label='Compras de clientes'
              value={userPurchasesTotal}
              icon={UserMembershipIcon}
              accent='green'
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default MembershipsSection;
